import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// ---------- Display helpers ----------
export function firstName(name) {
    if (!name) return "";
    return String(name).trim().split(/\s+/)[0];
}

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

// Attach Bearer token (fallback for environments where cookies don't flow)
api.interceptors.request.use((config) => {
    const tok = localStorage.getItem("milo_session_token");
    if (tok) config.headers.Authorization = `Bearer ${tok}`;
    return config;
});

// ---------- Auth ----------
export async function exchangeSessionId(session_id) {
    const { data } = await api.post("/auth/profile", { session_id });
    if (data.session_token) {
        localStorage.setItem("milo_session_token", data.session_token);
    }
    return data.user;
}
export async function fetchMe() {
    const { data } = await api.get("/auth/me");
    return data;
}
export async function logoutApi() {
    try {
        await api.post("/auth/logout");
    } catch (e) {
        // Logout is best-effort: if the server's already invalidated the
        // session we still clear local state below. Just log for visibility.
        console.warn("logout API call failed (clearing local session anyway):", e);
    }
    localStorage.removeItem("milo_session_token");
    localStorage.removeItem("milo_active_household");
}

// ---------- Active household selection (local) ----------
const ACTIVE_KEY = "milo_active_household";
export function saveActiveHousehold(s) {
    localStorage.setItem(ACTIVE_KEY, JSON.stringify(s));
}
export function loadActiveHousehold() {
    try {
        return JSON.parse(localStorage.getItem(ACTIVE_KEY) || "null");
    } catch {
        return null;
    }
}
export function clearActiveHousehold() {
    localStorage.removeItem(ACTIVE_KEY);
}

// ---------- Households ----------
export async function fetchMyHouseholds() {
    const { data } = await api.get("/me/households");
    return data;
}
export async function createHousehold(household_name) {
    const { data } = await api.post("/households", { household_name });
    return data;
}
export async function joinHousehold(code) {
    const { data } = await api.post("/households/join", { code });
    return data;
}
export async function deleteHousehold(household_id) {
    const { data } = await api.delete(`/households/${household_id}`);
    return data;
}
export async function leaveHousehold(household_id) {
    const { data } = await api.post(`/households/${household_id}/leave`);
    return data;
}
export async function fetchHousehold(household_id) {
    const { data } = await api.get(`/households/${household_id}`);
    return data;
}
export async function fetchWeek(household_id, weeks = 1) {
    const { data } = await api.get(`/households/${household_id}/weekly-plan`, { params: { weeks } });
    return data;
}
export async function setMeal(household_id, payload) {
    const { data } = await api.put(`/households/${household_id}/meals`, payload);
    return data;
}
export async function fetchShopping(household_id) {
    const { data } = await api.get(`/households/${household_id}/shopping`);
    return data;
}
export async function addShopping(household_id, payload) {
    // payload can be a string (legacy) or an object with name, quantity, personal, store, category
    const body = typeof payload === "string" ? { name: payload } : payload;
    const { data } = await api.post(`/households/${household_id}/shopping`, body);
    return data;
}
export async function updateShopping(household_id, item_id, updates) {
    const { data } = await api.patch(`/households/${household_id}/shopping/${item_id}`, updates);
    return data;
}
export async function toggleShopping(household_id, item_id, checked) {
    const { data } = await api.patch(`/households/${household_id}/shopping/${item_id}`, { checked });
    return data;
}
export async function deleteShopping(household_id, item_id) {
    const { data } = await api.delete(`/households/${household_id}/shopping/${item_id}`);
    return data;
}
export async function fetchStores(household_id) {
    const { data } = await api.get(`/households/${household_id}/stores`);
    return data.stores;
}
export async function addStore(household_id, name) {
    const { data } = await api.post(`/households/${household_id}/stores`, { name });
    return data;
}
// ---------- Recipes ----------
export async function fetchRecipes(household_id) {
    const { data } = await api.get(`/households/${household_id}/recipes`);
    return data.recipes;
}
export async function createRecipe(household_id, payload) {
    const { data } = await api.post(`/households/${household_id}/recipes`, payload);
    return data;
}
export async function updateRecipe(recipe_id, payload) {
    const { data } = await api.patch(`/recipes/${recipe_id}`, payload);
    return data;
}
export async function deleteRecipe(recipe_id) {
    const { data } = await api.delete(`/recipes/${recipe_id}`);
    return data;
}
export async function rateRecipe(recipe_id, stars) {
    const { data } = await api.post(`/recipes/${recipe_id}/rating`, { stars });
    return data;
}
export async function clearRecipeRating(recipe_id) {
    const { data } = await api.delete(`/recipes/${recipe_id}/rating`);
    return data;
}
// ---------- Household settings + Menu→Shopping ----------
export async function updateHouseholdSettings(household_id, payload) {
    const { data } = await api.patch(`/households/${household_id}/settings`, payload);
    return data;
}
export async function addMealToShopping(household_id, meal_id) {
    const { data } = await api.post(`/households/${household_id}/meals/${meal_id}/add-to-shopping`);
    return data;
}
export async function addWeekToShopping(household_id, week_start) {
    const params = week_start ? { week_start } : {};
    const { data } = await api.post(`/households/${household_id}/menu/add-week-to-shopping`, null, { params });
    return data;
}
export async function fixMyMenu(household_id, opts = {}) {
    const params = {};
    if (opts.week_start) params.week_start = opts.week_start;
    if (opts.overwrite) params.overwrite = true;
    const { data } = await api.post(`/households/${household_id}/menu/fix`, null, { params });
    return data;
}
export async function bulkCheckShopping(household_id, item_ids) {
    const { data } = await api.post(`/households/${household_id}/shopping/check-matched`, { item_ids });
    return data;
}
export async function saveMealToVault(household_id, meal_id) {
    const { data } = await api.post(`/households/${household_id}/meals/${meal_id}/save-to-vault`);
    return data;
}
export async function backfillMissingMealContent(household_id, week_start) {
    const params = {};
    if (week_start) params.week_start = week_start;
    const { data } = await api.post(`/households/${household_id}/meals/backfill-missing-content`, null, { params });
    return data;
}
export async function pickOneRecipe(household_id, prefer) {
    const params = {};
    if (prefer) params.prefer = prefer;
    const { data } = await api.post(`/households/${household_id}/menu/pick-one`, null, { params });
    return data;
}
export async function updateMember(member_id, payload) {
    const { data } = await api.patch(`/members/${member_id}`, payload);
    return data;
}
// ---------- Kitchen inventory ----------
export async function fetchStorageLocations(household_id) {
    const { data } = await api.get(`/households/${household_id}/storage-locations`);
    return data.locations;
}
export async function createStorageLocation(household_id, payload) {
    const { data } = await api.post(`/households/${household_id}/storage-locations`, payload);
    return data;
}
export async function updateStorageLocation(location_id, payload) {
    const { data } = await api.patch(`/storage-locations/${location_id}`, payload);
    return data;
}
export async function deleteStorageLocation(location_id, move_to) {
    const params = move_to ? { move_to } : {};
    const { data } = await api.delete(`/storage-locations/${location_id}`, { params });
    return data;
}
export async function fetchInventory(household_id, location_id) {
    const params = location_id ? { location_id } : {};
    const { data } = await api.get(`/households/${household_id}/inventory`, { params });
    return data.items;
}
export async function createInventoryItem(household_id, payload) {
    const { data } = await api.post(`/households/${household_id}/inventory`, payload);
    return data;
}
export async function updateInventoryItem(item_id, payload) {
    const { data } = await api.patch(`/inventory/${item_id}`, payload);
    return data;
}
export async function deleteInventoryItem(item_id) {
    const { data } = await api.delete(`/inventory/${item_id}`);
    return data;
}
export async function fetchFrequentInventory(household_id, limit = 5) {
    const { data } = await api.get(`/households/${household_id}/inventory/frequent`, { params: { limit } });
    return data;
}
export async function quickAddInventory(household_id, payload) {
    const { data } = await api.post(`/households/${household_id}/inventory/quick-add`, payload);
    return data;
}
// ---------- Stripe Billing ----------
export async function fetchBillingConfig() {
    const { data } = await api.get(`/billing/config`);
    return data;
}
export async function createCheckoutSession(return_path = "/app") {
    const { data } = await api.post(`/billing/checkout`, { return_path });
    return data; // { url, session_id }
}
export async function openBillingPortal() {
    const { data } = await api.post(`/billing/portal`);
    return data; // { url }
}
export async function confirmCheckoutSession(session_id) {
    const { data } = await api.get(`/billing/session/${session_id}`);
    return data;
}
export async function fetchChores(household_id) {
    const { data } = await api.get(`/households/${household_id}/chores`);
    return data;
}
export async function createChore(household_id, payload) {
    const { data } = await api.post(`/households/${household_id}/chores`, payload);
    return data;
}
export async function updateChore(household_id, chore_id, payload) {
    const { data } = await api.patch(`/households/${household_id}/chores/${chore_id}`, payload);
    return data;
}
export async function fetchToday(household_id) {
    const { data } = await api.get(`/households/${household_id}/today`);
    return data;
}
export async function fetchChat(household_id, member_id) {
    const { data } = await api.get(`/households/${household_id}/chat/${member_id}`);
    return data;
}
export async function postChat(member_id, text) {
    const { data } = await api.post(`/chat`, { member_id, text });
    return data;
}
export async function voiceOnboard(member_id, transcript) {
    const { data } = await api.post(`/voice/onboard`, { member_id, transcript });
    return data;
}
export async function submitFeedback(payload) {
    const { data } = await api.post(`/feedback`, payload);
    return data;
}

// ---------- Admin ----------
export async function fetchAdminOverview() {
    const { data } = await api.get(`/admin/overview`);
    return data;
}
export async function fetchAdminFamilies() {
    const { data } = await api.get(`/admin/families`);
    return data;
}
export async function fetchAdminFeedback(params = {}) {
    const { data } = await api.get(`/admin/feedback`, { params });
    return data;
}
export async function updateFeedbackStatus(feedback_id, status) {
    const { data } = await api.patch(`/admin/feedback/${feedback_id}`, { status });
    return data;
}
export async function deleteHouseholdAdmin(household_id) {
    const { data } = await api.delete(`/admin/households/${household_id}`);
    return data;
}
export async function fetchAdminFunnel(weeks = 8) {
    const { data } = await api.get(`/admin/activation-funnel`, { params: { weeks } });
    return data;
}
export async function fetchAdminStickiness(weeks = 8) {
    const { data } = await api.get(`/admin/stickiness`, { params: { weeks } });
    return data;
}
export async function fetchAdminWebhookStatus() {
    const { data } = await api.get(`/admin/webhooks/status`);
    return data;
}
export async function fetchAdminUsers() {
    const { data } = await api.get(`/admin/users`);
    return data;
}
export async function updateUserPlan(user_id, payload) {
    const { data } = await api.patch(`/admin/users/${user_id}/plan`, payload);
    return data;
}

// ---------- Magic Link ----------
export async function requestMagicLink(payload) {
    const { data } = await api.post(`/auth/magic-link`, payload);
    return data;
}
export async function verifyMagicLink(token) {
    const { data } = await api.post(`/auth/magic-verify`, { token });
    return data;
}
export async function setOwnPassword(new_password) {
    const { data } = await api.post(`/auth/set-password`, { new_password });
    return data;
}
